import { useEffect, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";
import { api } from "../../lib/api";
import { attachProp, propFor } from "./props3d";

const PREF = "baobab.bao3d";
const cache = {};

/** Validated GLB of a character (or, with `latest`, the newest generated one while none is validated). Null when unavailable. */
const load = (c, latest) => (cache[`${c}:${latest}`] ||= (async () => {
  const st = (await api.get(`/characters3d/${c}`)).data;
  const anim = (st.ready_jobs || []).filter((j) => j.source !== "upload" && j.files?.animations);
  const job = st.ready_jobs?.find((j) => j.id === st.approval?.job_id) || (latest ? anim.find((j) => propFor(st, j)) || anim.find((j) => !j.variant) || anim[0] : null);
  const fid = job?.files?.animations || job?.files?.rigged;
  if (!fid) return null;
  const gltf = await new GLTFLoader().loadAsync(`${process.env.REACT_APP_BACKEND_URL}/api/characters3d/files/${fid}`);
  gltf.animations.forEach((a) => { a.tracks = a.tracks.filter((t) => !/Hips\.position$/.test(t.name)); });
  const clips = Object.fromEntries((job.clips || []).map((x, i) => [x.key, gltf.animations[i]]).filter(([, a]) => a));
  return { gltf, clips, approved: st.approval?.job_id === job.id, jobId: job.id, prop: propFor(st, job) };
})().catch(() => null));

const webgl = () => { try { return !!document.createElement("canvas").getContext("webgl2"); } catch { return false; } };
export const bao3dEnabled = () => localStorage.getItem(PREF) !== "off" && webgl();
export const setBao3d = (on) => localStorage.setItem(PREF, on ? "on" : "off");

export const useCharacterModel = (character, latest = false) => {
  const [m, setM] = useState(undefined);
  useEffect(() => {
    if (!bao3dEnabled()) { setM(null); return; }
    let live = true;
    load(character, latest).then(async (r) => {
      if (!live) return;
      if (!r) return setM(null);
      const scene = cloneSkinned(r.gltf.scene), root = new THREE.Group();
      scene.updateMatrixWorld(true);
      const y = (n) => { const o = scene.getObjectByName(n); return o ? o.getWorldPosition(new THREE.Vector3()).y : null; };
      const low = Math.min(y("LeftToeBase"), y("RightToeBase"), y("LeftFoot"), y("RightFoot")), k = 1.5 / (y("head_end") - low);
      scene.scale.multiplyScalar(k); scene.position.y = -low * k;
      scene.traverse((o) => { if (o.isMesh) o.frustumCulled = false; });
      if (r.prop) await attachProp(scene, character, r.prop, r.clips.idle).catch(() => {});
      if (!live) return;
      root.add(scene);
      setM({ root, scene, clips: r.clips, approved: r.approved, jobId: r.jobId });
    });
    return () => { live = false; };
  }, [character, latest]);
  return m;
};

export const useBaoModel = () => useCharacterModel("bao");
